import { z } from 'zod';
import { InventoryStatus } from './status.ts';
import { InventoryImage, InventoryImageInput } from './image.ts';
import type { FinanceDisplay } from './finance.ts';
import type { ListingBadge } from './listing.ts';
import { TagColor, TagStyle } from './lookup.ts';

export const Condition = z.enum(['New', 'Used']);
export type Condition = z.infer<typeof Condition>;

export const INVENTORY_ID_PATTERN = /^[0-9]{6}-[0-9]{2}$/;

// Which unit `mileage` was typed in (migration 0011). NCC (2026-10-20): older
// classics read in miles, newer units in km. Null means not stated, and the
// site prints the bare number — what every older row shows.
export const MileageUnit = z.enum(['km', 'mi']);
export type MileageUnit = z.infer<typeof MileageUnit>;

// Which NCC lot the unit sits on (migration 0012). NCC (2026-10-23) opened a
// second trailer lot in Regina. Null means not stated, and the site shows no
// location line, which is what every older row shows.
export const InventoryLocation = z.enum(['Saskatoon', 'Regina']);
export type InventoryLocation = z.infer<typeof InventoryLocation>;

// Dimensions are not always a bare measurement — the real data carries
// qualifying notes ("70" when Ramp is Upwards; 31" when ramp is fold down",
// "Rear Ramp 93" WX 87" H with 18" Transition Ramp & Front Ramp 76"W X 83" H").
// The old 40-char cap rejected 4 of the 157 imported listings outright, and
// because the admin form PATCHes the whole record that made those 4 listings
// unsavable for *any* edit. 200 matches the sibling free-text spec fields.
const dimension = z.string().trim().max(200).nullable().optional();
const intish = z.number().int().nonnegative().nullable().optional();
const money = z.number().nonnegative().nullable().optional();

export const InventoryBase = z.object({
  inventory_id: z
    .string()
    .regex(INVENTORY_ID_PATTERN, 'Must be 6 digits, dash, 2 digits (e.g. 123456-24)'),
  year: z.number().int().min(1900).max(2100),
  regular_price: money,
  sale_price: money,
  // Optional manual override for the bi-weekly payment. When null, the widget
  // computes the estimate from the sale price (see finance.ts).
  biweekly_payment: money,

  // Per-listing financing off-switch. An off-switch only: it can take financing
  // away from a listing that qualifies (a new trailer quoted case-by-case), but
  // cannot grant it to a used unit or a classic vehicle — the type/condition
  // rule is still the floor. See isFinanced in listing.ts.
  financing_disabled: z.boolean().optional(),

  // Full VIN. Private: stored for record-keeping but never returned by the
  // public API. Optional — drafts and duplicated listings leave it blank.
  vin: z.string().trim().max(17).nullable().optional(),

  brand_id: z.number().int().positive().nullable(),
  type_id: z.number().int().positive().nullable(),
  tag_id: z.number().int().positive().nullable().optional(),

  // Quotes are allowed: trailer names are full of feet/inch marks (12' Single
  // Axle Utility, 82"X20' Speedloader). The widget escapes every interpolation
  // through htmlSafe() and buildSlug() strips non-alphanumerics, so nothing
  // downstream needs them banned. An earlier ban here made the ~80 CSV-imported
  // listings unsavable through the admin form — the form PATCHes the whole
  // record, so a name it wouldn't accept blocked edits to every other field.
  vehicle_name: z.string().min(1).max(200),
  model_number: z.string().min(1).max(100),
  mileage: intish,
  mileage_unit: MileageUnit.nullable().optional(),
  location: InventoryLocation.nullable().optional(),
  color: z.string().max(60).nullable().optional(),
  condition: Condition,
  youtube_link: z.string().url().nullable().optional().or(z.literal('')),
  description: z.string().min(1).max(20_000),

  gawr: intish,
  gross_capacity: intish,
  curb_weight: intish,
  payload_capacity: intish,

  tie_downs: z.string().max(200).nullable().optional(),
  lights: z.string().max(200).nullable().optional(),
  hitch: z.string().max(200).nullable().optional(),
  jack: z.string().max(200).nullable().optional(),
  axles: z.string().max(200).nullable().optional(),
  suspensions: z.string().max(200).nullable().optional(),
  tires_wheels: z.string().max(200).nullable().optional(),
  // Also descriptive in practice ("Aluminum 8.5" x 22.5" Hub Piloted Wheels,
  // Aloca Polished (outers only)") — same 40-char problem as the dimensions.
  rim_size: z.string().max(200).nullable().optional(),
  trailer_plug: z.string().max(200).nullable().optional(),
  ramp: z.string().max(200).nullable().optional(),
  frame_construction: z.string().max(200).nullable().optional(),
  deck_construction: z.string().max(200).nullable().optional(),

  overall_length: dimension,
  overall_width: dimension,
  overall_height: dimension,
  interior_length: dimension,
  interior_width: dimension,
  interior_height: dimension,
  deck_length: dimension,
  deck_width: dimension,
  deck_height: dimension,
  enclosed_door_size: dimension,
});

export const InventoryCreateInput = InventoryBase.extend({
  status: InventoryStatus.default('draft'),
  images: z.array(InventoryImageInput).default([]),
});
export type InventoryCreateInput = z.infer<typeof InventoryCreateInput>;

export const InventoryUpdateInput = InventoryBase.partial().extend({
  status: InventoryStatus.optional(),
  sold_at: z.string().datetime().nullable().optional(),
});
export type InventoryUpdateInput = z.infer<typeof InventoryUpdateInput>;

export const DraftInventoryInput = InventoryBase.partial().extend({
  status: z.literal('draft').default('draft'),
  images: z.array(InventoryImageInput).optional(),
});
export type DraftInventoryInput = z.infer<typeof DraftInventoryInput>;

export const Inventory = InventoryBase.extend({
  id: z.number().int().positive(),
  slug: z.string(),
  status: InventoryStatus,
  sold_at: z.string().datetime().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  published_at: z.string().datetime().nullable(),
  brand_name: z.string().nullable(),
  type_name: z.string().nullable(),
  tag_name: z.string().nullable(),
  // The tag's style (see TagStyle); null when the listing has no tag.
  tag_style: TagStyle.nullable(),
  // The tag's chip colour (see TagColor); null when the listing has no tag.
  tag_color: TagColor.nullable(),
  images: z.array(InventoryImage),
});
export type Inventory = z.infer<typeof Inventory>;

/**
 * A listing as the public API serves it: private columns dropped, and the
 * financing decision attached already made (see financeDisplay in finance.ts).
 *
 * Consumers must read `financed` / `biweekly_display` / `lease_monthly_display`
 * rather than recomputing from price, condition and type — `financing_disabled`
 * is not in this shape, so anything re-deriving the gate silently ignores the
 * per-listing off-switch and shows a payment staff turned off. That applies to
 * our own widgets exactly as much as to the site connector.
 *
 * Likewise `badges` (see listingBadges): render the list as given, in order,
 * and `key_features` (see listingKeyFeatures) beside the price.
 */
export type PublicInventory = Omit<Inventory, 'vin' | 'financing_disabled'> &
  FinanceDisplay & { badges: ListingBadge[]; key_features: string[] };

export const BulkActionInput = z.object({
  action: z.enum(['publish', 'hide', 'mark_sold', 'delete']),
  ids: z.array(z.number().int().positive()).min(1).max(500),
});
export type BulkActionInput = z.infer<typeof BulkActionInput>;

// A comma-separated list of positive integer ids from a query string, parsed to
// number[] with blanks/garbage dropped. Absent → undefined; all-garbage → [].
const IdList = z
  .string()
  .transform((s) =>
    s
      .split(',')
      .map((x) => parseInt(x.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0),
  )
  .optional();

export const InventoryListQuery = z.object({
  q: z.string().trim().optional(),
  status: z.union([InventoryStatus, z.array(InventoryStatus)]).optional(),
  brand_id: z.coerce.number().int().positive().optional(),
  type_id: z.coerce.number().int().positive().optional(),
  // Exclude a single inventory type. Used by the public trailer catalogue to
  // hide the classic-vehicles type ("all trailers, no cars") without needing to
  // enumerate every trailer type id.
  exclude_type_id: z.coerce.number().int().positive().optional(),
  // Comma-separated type id lists (e.g. "9,11"). The catalogue collections need
  // these because the classic vehicles live under more than one type name
  // ("Vehicle" AND "Vehicles"): trailers-only excludes them all, the classic
  // embed includes them all. Empty/garbage entries are dropped.
  type_ids: IdList,
  exclude_type_ids: IdList,
  // Promo/display tag (the freeform `tags` lookup — "Harvest Sale", "Call for
  // Quote", …). A listing carries at most one, so this is a single id rather
  // than a list. Drives the catalogue Tag filter and the ?tag= deep links the
  // site uses for seasonal promos.
  tag_id: z.coerce.number().int().positive().optional(),
  condition: Condition.optional(),
  min_year: z.coerce.number().int().optional(),
  max_year: z.coerce.number().int().optional(),
  min_price: z.coerce.number().nonnegative().optional(),
  max_price: z.coerce.number().nonnegative().optional(),
  min_gvwr: z.coerce.number().nonnegative().optional(),
  max_gvwr: z.coerce.number().nonnegative().optional(),
  inventory_id: z.string().optional(),
  // Exact-match VIN lookup, for the dashboard's "is this VIN already taken?"
  // check. Exact rather than LIKE on purpose: it has to mirror the unique index
  // that would reject the save, and a partial match would warn about VINs that
  // would actually store fine.
  //
  // ADMIN ONLY. The public list query omits this (see routes/public.ts) — VIN is
  // stripped from public payloads, so accepting a public ?vin= filter would hand
  // back a yes/no oracle for any VIN someone cared to guess.
  vin: z.string().trim().optional(),
  sort: z
    .enum(['newest', 'oldest', 'price_asc', 'price_desc', 'year_desc', 'year_asc'])
    .default('newest'),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(100).default(20),
});
export type InventoryListQuery = z.infer<typeof InventoryListQuery>;
